import React, { useEffect, useRef, useState } from 'react'

const processSteps = [
  {
    number: "01",
    title: "Collection of Birth Details",
    icon: "📋",
    points: [
      "Full name as used in daily life and legal documents",
      "Date of Birth for accurate planetary position calculation",
      "Exact Time of Birth for rising sign and house determination",
      "Place of Birth for geographical coordinates and precise chart casting",
    ],
  },
  {
    number: "02",
    title: "Collection of Current Concerns & Queries",
    icon: "💬",
    points: [
      "Understanding present challenges in career, finances or relationships",
      "Identifying recurring patterns or unresolved life situations",
      "Noting specific questions around health, marriage, growth or timing",
      "Clarifying the key areas of life requiring insight and direction",
    ],
  },
  {
    number: "03",
    title: "Preparation & Analysis of Birth Chart (Kundli)",
    icon: "🔵",
    points: [
      "Casting of the Lagna (Ascendant) chart based on exact birth data",
      "Preparation of Navamsa, Dashamsa and other divisional charts",
      "Identification of all twelve house lords and their placements",
      "Mapping of planetary conjunctions, aspects and sign dispositions",
    ],
  },
  {
    number: "04",
    title: "Study of Planetary Positions & Influences",
    icon: "🪐",
    points: [
      "Detailed examination of all nine planets and their sign strength",
      "Analysis of benefic and malefic planetary influences on key houses",
      "Study of current Mahadasha, Antardasha and planetary transit effects",
      "Identification of Yogas — auspicious and challenging planetary combinations",
    ],
  },
  {
    number: "05",
    title: "Personalised Suggestions Based on Planetary Analysis",
    icon: "✨",
    points: [
      "Tailored guidance for career, business and financial decisions",
      "Relationship and compatibility insights based on chart analysis",
      "Gemstone, color and direction recommendations aligned with planetary strengths",
      "Remedial measures including mantras, rituals and charitable acts for balance",
    ],
  },
  {
    number: "06",
    title: "Further Guidance for Better Decisions & Life Balance",
    icon: "🌟",
    points: [
      "Timing guidance for important life decisions using Dasha and transit analysis",
      "Practical lifestyle suggestions to align with planetary rhythms",
      "Guidance on favorable periods for new beginnings, investments and relationships",
      "Follow-up support to clarify doubts and track progress over time",
    ],
  },
]

const planets = [
  { symbol: "☉", name: "Sun", rules: "Soul & Authority", color: "#f9c74f" },
  { symbol: "☽", name: "Moon", rules: "Mind & Emotions", color: "#c3aed6" },
  { symbol: "♂", name: "Mars", rules: "Energy & Courage", color: "#ff6b6b" },
  { symbol: "☿", name: "Mercury", rules: "Intellect & Speech", color: "#90e0ef" },
  { symbol: "♃", name: "Jupiter", rules: "Wisdom & Fortune", color: "#f4a261" },
  { symbol: "♀", name: "Venus", rules: "Love & Luxury", color: "#b5ead7" },
  { symbol: "♄", name: "Saturn", rules: "Karma & Discipline", color: "#adb5bd" },
  { symbol: "☊", name: "Rahu", rules: "Desires & Illusion", color: "#9d4edd" },
  { symbol: "☋", name: "Ketu", rules: "Moksha & Liberation", color: "#e76f51" },
]

const chartTypes = [
  { icon: "🔵", name: "Lagna Chart", desc: "The primary birth chart revealing your core personality, life path and overall destiny" },
  { icon: "💠", name: "Navamsa (D-9)", desc: "Reveals marriage, dharma and the deeper spiritual purpose behind your life journey" },
  { icon: "🔷", name: "Dashamsa (D-10)", desc: "The career and professional chart showing your natural vocation and worldly achievements" },
  { icon: "🟣", name: "Divisional Charts", desc: "Specialized charts for health, wealth, children and other specific areas of life" },
]

const ProcessCard = ({ step, index }) => {
  const [visible, setVisible] = useState(false)
  const ref = useRef()

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true) },
      { threshold: 0.15 }
    )
    if (ref.current) observer.observe(ref.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(40px)',
        transition: `opacity 0.7s ease ${index * 0.1}s, transform 0.7s ease ${index * 0.1}s`,
        background: 'linear-gradient(135deg, rgba(130,94,10,0.08) 0%, rgba(255,255,255,0.95) 100%)',
        border: '1px solid rgba(130,94,10,0.18)',
        borderLeft: '4px solid #825e0a',
        borderRadius: '12px',
        padding: '32px 28px',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
        boxShadow: '0 4px 24px rgba(130,94,10,0.07)',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 12px 40px rgba(130,94,10,0.18)'
        e.currentTarget.style.borderLeftColor = '#c48a14'
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = '0 4px 24px rgba(130,94,10,0.07)'
        e.currentTarget.style.borderLeftColor = '#825e0a'
      }}
    >
      <div style={{
        position: 'absolute', top: '-10px', right: '16px',
        fontSize: '90px', fontFamily: '"Playfair Display", serif',
        color: 'rgba(130,94,10,0.06)', fontWeight: '900',
        lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
      }}>
        {step.number}
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div style={{
          width: '52px', height: '52px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #825e0a, #c48a14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '22px', flexShrink: 0,
          boxShadow: '0 4px 12px rgba(130,94,10,0.3)',
        }}>
          {step.icon}
        </div>
        <div>
          <span style={{
            fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Step {step.number}
          </span>
          <h3 style={{
            margin: '4px 0 0', fontSize: '18px', fontWeight: '700',
            color: '#2c1a00', fontFamily: '"Playfair Display", serif', lineHeight: '1.3',
          }}>
            {step.title}
          </h3>
        </div>
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {step.points.map((point, i) => (
          <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
            <span style={{
              width: '18px', height: '18px', borderRadius: '50%',
              background: 'rgba(130,94,10,0.1)', border: '1.5px solid rgba(130,94,10,0.35)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, marginTop: '2px',
            }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#825e0a', display: 'block' }} />
            </span>
            <span style={{ fontSize: '14px', color: '#5a3d10', lineHeight: '1.6', fontFamily: '"Lato", sans-serif' }}>
              {point}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const Astro = () => {
  const [headerVisible, setHeaderVisible] = useState(false)
  const [planetsVisible, setPlanetsVisible] = useState(false)
  const [chartsVisible, setChartsVisible] = useState(false)
  const planetsRef = useRef()
  const chartsRef = useRef()

  useEffect(() => {
    const t = setTimeout(() => setHeaderVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    const obs1 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setPlanetsVisible(true) }, { threshold: 0.1 })
    const obs2 = new IntersectionObserver(([e]) => { if (e.isIntersecting) setChartsVisible(true) }, { threshold: 0.15 })
    if (planetsRef.current) obs1.observe(planetsRef.current)
    if (chartsRef.current) obs2.observe(chartsRef.current)
    return () => { obs1.disconnect(); obs2.disconnect() }
  }, [])

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=Montserrat:wght@400;600;700&family=Lato:wght@300;400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #fdf8f0; }

        .astro-section { min-height: 100vh; background: #fdf8f0; font-family: 'Lato', sans-serif; overflow: hidden; }

        .astro-hero {
          position: relative;
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 40%, #825e0a 100%);
          padding: 80px 24px 100px;
          text-align: center;
          overflow: hidden;
        }

        .mandala-ring {
          position: absolute; border-radius: 50%;
          border: 1px solid rgba(196,138,20,0.12);
          top: 50%; left: 50%; pointer-events: none;
        }
        .spin { animation: rotateSlow 60s linear infinite; }
        .spin-rev { animation: rotateSlowRev 80s linear infinite; }
        @keyframes rotateSlow { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(360deg); } }
        @keyframes rotateSlowRev { from { transform: translate(-50%,-50%) rotate(0deg); } to { transform: translate(-50%,-50%) rotate(-360deg); } }

        /* Orbiting stars in hero */
        .star-orbit {
          position: absolute; top: 50%; left: 50%;
          width: 600px; height: 600px;
          margin-left: -300px; margin-top: -300px;
          border-radius: 50%;
          animation: rotateSlow 40s linear infinite;
          pointer-events: none;
        }
        .star-dot {
          position: absolute; top: 0; left: 50%;
          width: 4px; height: 4px; border-radius: 50%;
          background: #f0c060; opacity: 0.6;
          transform: translateX(-50%);
          box-shadow: 0 0 6px #f0c060;
        }

        .badge {
          display: inline-block;
          background: rgba(196,138,20,0.15);
          border: 1px solid rgba(196,138,20,0.4);
          color: #f0c060; padding: 6px 18px; border-radius: 40px;
          font-size: 11px; letter-spacing: 3px; text-transform: uppercase;
          font-family: 'Montserrat', sans-serif; font-weight: 700; margin-bottom: 20px;
        }

        .sub-badges { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-top: 24px; }
        .sub-badge {
          background: rgba(253,248,240,0.08); border: 1px solid rgba(240,192,96,0.25);
          color: rgba(253,248,240,0.7); padding: 6px 16px; border-radius: 30px;
          font-size: 12px; font-family: 'Montserrat', sans-serif; font-weight: 600; letter-spacing: 0.5px;
        }

        .divider-row { display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 12px; }
        .line-r { height: 1px; width: 60px; background: linear-gradient(to right, transparent, #c48a1488, #c48a14); }
        .line-l { height: 1px; width: 60px; background: linear-gradient(to left, transparent, #c48a1488, #c48a14); }

        /* Planets strip */
        .planets-strip {
          background: linear-gradient(160deg, #2c1a00 0%, #4a2e05 60%, #3a2204 100%);
          padding: 72px 24px;
          overflow: hidden;
        }
        .planets-scroll {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          justify-content: center;
          max-width: 1100px;
          margin: 40px auto 0;
        }
        .planet-pill {
          display: flex; align-items: center; gap: 12px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(196,138,20,0.2);
          border-radius: 50px;
          padding: 12px 20px;
          transition: transform 0.3s ease, background 0.3s ease;
          cursor: default;
        }
        .planet-pill:hover { transform: translateY(-4px); background: rgba(255,255,255,0.1); }

        /* Charts strip */
        .charts-strip {
          background: #fdf8f0;
          padding: 72px 24px;
        }
        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 20px; max-width: 960px; margin: 40px auto 0;
        }
        .chart-card {
          background: linear-gradient(135deg, rgba(130,94,10,0.06), rgba(255,255,255,0.9));
          border: 1px solid rgba(130,94,10,0.15);
          border-top: 3px solid #825e0a;
          border-radius: 12px; padding: 28px 20px; text-align: center;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          cursor: default;
        }
        .chart-card:hover { transform: translateY(-5px); box-shadow: 0 12px 32px rgba(130,94,10,0.15); }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px; max-width: 1100px; margin: 0 auto; padding: 48px 24px 64px;
        }

        .section-heading { text-align: center; padding: 64px 24px 0; }

        .cta-strip {
          background: linear-gradient(135deg, #825e0a, #4a2e05);
          padding: 56px 24px; text-align: center; position: relative; overflow: hidden;
        }
        .cta-strip::before { content: '✦'; position: absolute; top: 24px; left: 40px; color: rgba(255,255,255,0.06); font-size: 60px; }
        .cta-strip::after  { content: '✦'; position: absolute; bottom: 24px; right: 40px; color: rgba(255,255,255,0.06); font-size: 60px; }

        .cta-btn {
          display: inline-block;
          background: linear-gradient(135deg, #f0c060, #c48a14);
          color: #2c1a00; padding: 16px 44px; border-radius: 50px;
          font-family: 'Montserrat', sans-serif; font-weight: 700; font-size: 15px;
          letter-spacing: 1px; border: none; cursor: pointer;
          box-shadow: 0 8px 28px rgba(196,138,20,0.4);
          transition: transform 0.25s ease, box-shadow 0.25s ease; text-decoration: none;
        }
        .cta-btn:hover { transform: translateY(-3px); box-shadow: 0 14px 36px rgba(196,138,20,0.55); }
      `}</style>

      <section className="astro-section">

        {/* Hero */}
        <div className="astro-hero">
          {[500, 700, 900].map((s, i) => (
            <div key={i} className={`mandala-ring ${i % 2 === 0 ? 'spin' : 'spin-rev'}`} style={{ width: s, height: s }} />
          ))}

          {/* Orbiting star dots */}
          {[0, 60, 120, 180, 240, 300].map((deg, i) => (
            <div key={i} className="star-orbit" style={{ animationDelay: `${-i * 6}s`, animationDuration: `${35 + i * 5}s`, opacity: 0.5 + i * 0.05 }}>
              <div className="star-dot" style={{ transform: `translateX(-50%) rotate(${deg}deg) translateY(-300px)` }} />
            </div>
          ))}

          <div style={{
            position: 'relative', zIndex: 2,
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(-30px)',
            transition: 'opacity 0.9s ease, transform 0.9s ease',
          }}>
            <div className="badge">✦ Vedic Astrology ✦</div>

            <h1 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(30px, 5vw, 52px)',
              fontWeight: '900', color: '#fdf8f0',
              lineHeight: '1.2', marginBottom: '16px',
              textShadow: '0 2px 20px rgba(0,0,0,0.3)',
            }}>
              Astrology Analysis
              <br />
              <span style={{ color: '#f0c060' }}>Your Cosmic Blueprint</span>
            </h1>

            <div className="divider-row">
              <div className="line-l" />
              <span style={{ color: '#c48a14', fontSize: '18px' }}>✦</span>
              <div className="line-r" />
            </div>

            <p style={{
              marginTop: '20px', color: 'rgba(253,248,240,0.75)',
              fontSize: '17px', fontFamily: '"Lato", sans-serif',
              fontWeight: '300', letterSpacing: '0.5px',
              maxWidth: '540px', margin: '20px auto 0', lineHeight: '1.8',
            }}>
              Understanding the Planetary Forces That Shape Your Life
            </p>

            <div className="sub-badges">
              <span className="sub-badge">🔵 Kundli Analysis</span>
              <span className="sub-badge">🪐 Planetary Study</span>
              <span className="sub-badge">📅 Dasha Timing</span>
              <span className="sub-badge">💎 Remedial Guidance</span>
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '60px' }}>
              <path d="M0,60 C360,0 1080,0 1440,60 L1440,60 L0,60 Z" fill="#fdf8f0" />
            </svg>
          </div>
        </div>

        {/* Intro */}
        <div style={{ maxWidth: '820px', margin: '0 auto', padding: '64px 24px 0', textAlign: 'center' }}>
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            display: 'block', marginBottom: '16px',
          }}>
            What is Astrology Analysis?
          </span>
          <p style={{
            fontSize: '16px', color: '#5a3d10', lineHeight: '1.9',
            fontFamily: '"Lato", sans-serif', fontWeight: '300',
          }}>
            Vedic Astrology is the ancient science of reading the <strong style={{ color: '#825e0a', fontWeight: '700' }}>cosmic blueprint</strong> encoded in the positions of planets at the precise moment of your birth. Your <strong style={{ color: '#825e0a' }}>Kundli</strong> — or birth chart — is a unique map of the sky that reveals your personality, karmic patterns, strengths and life purpose. Through the study of <strong style={{ color: '#825e0a' }}>planetary positions, Dashas and transits</strong>, we provide personalized insights to help you navigate life's most important decisions with clarity, confidence and cosmic alignment.
          </p>
        </div>

        {/* Birth Chart Types — light */}
        <div ref={chartsRef} className="charts-strip">
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            }}>
              Charts We Analyse
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(22px, 3vw, 34px)',
              color: '#2c1a00', fontWeight: '700', marginTop: '8px',
            }}>
              Your Complete Astrological Picture
            </h2>
            <div className="divider-row" style={{ marginTop: '12px' }}>
              <div className="line-l" />
              <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
              <div className="line-r" />
            </div>
          </div>
          <div className="charts-grid">
            {chartTypes.map((chart, i) => (
              <div
                key={i}
                className="chart-card"
                style={{
                  opacity: chartsVisible ? 1 : 0,
                  transform: chartsVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s`,
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '14px' }}>{chart.icon}</div>
                <h4 style={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: '17px', color: '#2c1a00',
                  fontWeight: '700', marginBottom: '10px',
                }}>
                  {chart.name}
                </h4>
                <p style={{ fontSize: '13px', color: '#5a3d10', fontFamily: '"Lato", sans-serif', lineHeight: '1.7' }}>
                  {chart.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Nine Planets — dark */}
        <div ref={planetsRef} className="planets-strip">
          <div style={{ textAlign: 'center' }}>
            <span style={{
              fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
              color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
            }}>
              The Nine Planets
            </span>
            <h2 style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 'clamp(22px, 3vw, 34px)',
              color: '#fdf8f0', fontWeight: '700', marginTop: '8px',
            }}>
              Navagrahas — Cosmic Influencers
            </h2>
            <div className="divider-row" style={{ marginTop: '12px' }}>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to right, transparent, #c48a1488, #c48a14)' }} />
              <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
              <div style={{ height: '1px', width: '60px', background: 'linear-gradient(to left, transparent, #c48a1488, #c48a14)' }} />
            </div>
            <p style={{
              marginTop: '16px', color: 'rgba(253,248,240,0.6)',
              fontSize: '15px', fontFamily: '"Lato", sans-serif',
              maxWidth: '520px', margin: '16px auto 0', lineHeight: '1.8',
            }}>
              Each of the nine planets governs a unique domain of your life — we study all of them to give you the complete picture.
            </p>
          </div>
          <div className="planets-scroll">
            {planets.map((p, i) => (
              <div
                key={i}
                className="planet-pill"
                style={{
                  opacity: planetsVisible ? 1 : 0,
                  transform: planetsVisible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.5s ease ${i * 0.07}s, transform 0.5s ease ${i * 0.07}s`,
                  borderColor: `${p.color}33`,
                }}
              >
                <span style={{
                  fontSize: '28px', color: p.color,
                  filter: 'drop-shadow(0 0 6px currentColor)',
                  fontFamily: 'serif', lineHeight: 1,
                }}>
                  {p.symbol}
                </span>
                <div>
                  <div style={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: '15px', fontWeight: '700', color: '#f0c060',
                  }}>
                    {p.name}
                  </div>
                  <div style={{
                    fontSize: '12px', color: 'rgba(253,248,240,0.55)',
                    fontFamily: '"Lato", sans-serif', marginTop: '2px',
                  }}>
                    {p.rules}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Steps */}
        <div className="section-heading">
          <span style={{
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            color: '#c48a14', fontWeight: '700', fontFamily: '"Montserrat", sans-serif',
          }}>
            Our Process
          </span>
          <h2 style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(24px, 3vw, 36px)',
            color: '#2c1a00', fontWeight: '700', marginTop: '8px',
          }}>
            How We Read Your Cosmic Blueprint
          </h2>
          <div className="divider-row">
            <div className="line-l" />
            <span style={{ color: '#c48a14', fontSize: '16px' }}>✦</span>
            <div className="line-r" />
          </div>
        </div>

        <div className="steps-grid">
          {processSteps.map((step, i) => (
            <ProcessCard key={step.number} step={step} index={i} />
          ))}
        </div>

        {/* CTA */}
        <div className="cta-strip">
          <p style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 'clamp(22px, 3vw, 32px)',
            color: '#fdf8f0', marginBottom: '8px', fontWeight: '700',
          }}>
            Discover What the Stars Say About You
          </p>
          <p style={{
            color: 'rgba(253,248,240,0.65)', fontSize: '15px',
            fontFamily: '"Lato", sans-serif', marginBottom: '32px', letterSpacing: '0.5px',
          }}>
            Book a personalised Vedic Astrology consultation and unlock your cosmic blueprint
          </p>
          <a href="https://wa.me/919122397324?text=Hello%20Tarun%2C%20I%E2%80%99m%20interested%20in%20Astrology%20Analysis." className="cta-btn">
            Book a Consultation ✦
          </a>
        </div>

      </section>
    </>
  )
}

export default Astro